import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface PushSubscriptionState {
  isSupported: boolean;
  isSubscribed: boolean;
  isLoading: boolean;
  permission: NotificationPermission | null;
}

/**
 * Hook for managing Web Push Notification subscriptions
 * Handles subscription lifecycle and storage in Supabase
 */
export function usePushNotifications() {
  const [state, setState] = useState<PushSubscriptionState>({
    isSupported: false,
    isSubscribed: false,
    isLoading: true,
    permission: null,
  });

  // Check if push notifications are supported
  useEffect(() => {
    const checkSupport = async () => {
      const isSupported = 
        'serviceWorker' in navigator && 
        'PushManager' in window &&
        'Notification' in window;
      
      if (!isSupported) {
        setState(prev => ({ ...prev, isSupported: false, isLoading: false }));
        return;
      }

      const permission = Notification.permission;
      
      // Check if already subscribed
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await (registration as any).pushManager.getSubscription();
        
        setState({
          isSupported: true,
          isSubscribed: !!subscription,
          isLoading: false,
          permission,
        });
      } catch (error) {
        console.error('Error checking push subscription:', error);
        setState(prev => ({ ...prev, isSupported: true, isLoading: false, permission }));
      }
    };

    checkSupport();
  }, []);

  // Convert VAPID key from base64 to ArrayBuffer
  const urlBase64ToUint8Array = useCallback((base64String: string): ArrayBuffer => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray.buffer as ArrayBuffer;
  }, []);

  // Subscribe to push notifications
  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!state.isSupported) {
      toast.error('Push notifications not supported', {
        description: 'Your browser does not support push notifications.'
      });
      return false;
    }

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      
      if (permission !== 'granted') {
        toast.error('Notifications blocked', {
          description: 'Please enable notifications in your browser settings.'
        });
        setState(prev => ({ ...prev, isLoading: false, permission }));
        return false;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Get VAPID public key from edge function
      const { data: vapidData, error: vapidError } = await supabase.functions.invoke('save-push-subscription', {
        body: { action: 'get-vapid-key' }
      });

      if (vapidError || !vapidData?.vapidPublicKey) {
        throw new Error('Failed to get VAPID key');
      }

      // Subscribe to push manager
      const subscription = await (registration as any).pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidData.vapidPublicKey)
      });

      // Extract subscription details
      const subscriptionJson = subscription.toJSON();
      const p256dh = subscriptionJson.keys?.p256dh || '';
      const auth = subscriptionJson.keys?.auth || '';

      // Save subscription to Supabase
      const { error: saveError } = await supabase.functions.invoke('save-push-subscription', {
        body: {
          action: 'subscribe',
          endpoint: subscription.endpoint,
          p256dh_key: p256dh,
          auth_key: auth,
          user_agent: navigator.userAgent
        }
      });

      if (saveError) {
        throw saveError;
      }

      // Link this device to the signed-in user so personal alerts (daily report) reach it.
      const { data: sess } = await supabase.auth.getSession();
      if (sess.session) {
        await supabase.functions.invoke('save-swyftx-key', { body: { action: 'link_push', endpoint: subscription.endpoint } });
      }

      setState(prev => ({ 
        ...prev, 
        isSubscribed: true, 
        isLoading: false,
        permission: 'granted'
      }));
      
      toast.success('Notifications enabled!', {
        description: "You'll receive alerts for breaking crypto news."
      });
      
      return true;
    } catch (error) {
      console.error('Error subscribing to push:', error);
      toast.error('Failed to enable notifications', {
        description: 'Please try again later.'
      });
      setState(prev => ({ ...prev, isLoading: false }));
      return false;
    }
  }, [state.isSupported, urlBase64ToUint8Array]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await (registration as any).pushManager.getSubscription();

      if (subscription) {
        // Unsubscribe from push manager
        await subscription.unsubscribe();

        // Remove from Supabase
        await supabase.functions.invoke('save-push-subscription', {
          body: {
            action: 'unsubscribe',
            endpoint: subscription.endpoint
          }
        });
      }

      setState(prev => ({ ...prev, isSubscribed: false, isLoading: false }));
      
      toast.success('Notifications disabled', {
        description: 'You will no longer receive push notifications.'
      });
      
      return true;
    } catch (error) {
      console.error('Error unsubscribing from push:', error);
      toast.error('Failed to disable notifications');
      setState(prev => ({ ...prev, isLoading: false }));
      return false;
    }
  }, []);

  return {
    ...state,
    subscribe,
    unsubscribe,
  };
}
